import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { formatNumber } from '../utils/format';
import LiquidityChart from '../components/LiquidityChart';
import BfreBars from '../components/BfreBars';
import { useForecastAssumptions } from '../hooks/useForecastAssumptions';
import { getPlView } from '../api/financials_pl';
import { getBsView } from '../api/financials_bs';
import { listIndirectCosts } from '../api/indirectCosts';
import { getPlannedFte } from '../api/hrPlanningFte';
import { liquiditySustainability } from './financials/synthesisModel';
import { sum12 } from './forecastModel';
import { usePersistentValue } from '../hooks/usePersistentState';
import Card from '../components/Card';
import './ForecastSummary.css';

/* Synthèse (Rapports) — vue direction CONDENSÉE au réel. Deux blocs :
     1. Compte de résultat simplifié (CA net → EBITDA, + marges en % du CA net)
     2. Cash flow opérationnel : Résultat net + Amortissements (= CAF) → variation
        du BFR détaillée → CF opérationnel.
   Colonnes (par groupe) : « 1 mois » (N-1 · N · écart CHF · écart %) puis
   « Cumulé » (N-1 · N phare · écart CHF · écart %).

   ⚠️ Le bloc cash flow est aligné EXACTEMENT sur l'onglet « Flux de trésorerie » :
   même point de départ (CAF = résultat net + amortissements) et même variation du
   BFR, sourcée de la classification Cash Flow du bilan (`cf_category`
   wcr_asset/wcr_liability). Le périmètre wcr est plus large que la BFRE restreinte
   (il inclut dettes sociales/fiscales, régularisations…). Le total « Cash flow
   opérationnel » tombe donc au franc près sur celui de l'onglet Flux.
   Le détail mensuel ligne-à-ligne vit dans les onglets dédiés (CR, Bilan, Flux). */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const findCode = (rows, code) => (rows || []).find((r) => r.code === code) || null;
const monthsOf = (rows, code) => {
  const r = findCode(rows, code);
  return Array.from({ length: 12 }, (_, i) => num(r?.months?.[String(i + 1)]));
};
// Décomposition du CA par type de revenu (marqueur revenue_type des lignes détail) :
// marchandises / prestations / autres (null → 'autres'). Même source que le prévisionnel.
const revByType = (rws, type) => Array.from({ length: 12 }, (_, m) =>
  (rws || [])
    .filter((r) => r.type === 'detail' && r.section === 'revenue' && (r.revenue_type || 'autres') === type)
    .reduce((a, r) => a + num(r.months?.[String(m + 1)]), 0));

/* Variation mensuelle du BFR (impact trésorerie), sourcée comme l'onglet Flux :
   pour chaque ligne de bilan taguée wcr_asset / wcr_liability, impact = −Δ(actif)
   ou +Δ(passif), avec l'ouverture (déc. N-1) reprise du bilan précédent. Regroupé
   en 4 postes (créances-clients / stocks / fournisseurs / autres) ; `autres` est le
   reliquat → le total colle toujours avec « Variation nette du BFR » du tableau Flux,
   quel que soit le rangement. Renvoie 4 séries de 12 mois + le total. */
const wcrCashImpact = (bsView, priorView) => {
  if (!bsView) return null;
  const catOf = {};
  (bsView.lines || []).forEach((l) => { catOf[l.id] = l.cf_category; });
  const priorByCode = {};
  ((priorView?.rows) || []).filter((r) => r.type === 'detail').forEach((r) => { priorByCode[r.code] = r; });
  const openOf = (code) => num(priorByCode[code]?.months?.['12']);   // solde déc. N-1
  const g = () => Array.from({ length: 12 }, () => 0);
  const out = { creances: g(), stocks: g(), fournisseurs: g(), autres: g(), total: g() };
  (bsView.rows || []).filter((r) => r.type === 'detail').forEach((r) => {
    const cat = catOf[r.code];
    if (cat !== 'wcr_asset' && cat !== 'wcr_liability') return;
    const sign = cat === 'wcr_asset' ? -1 : 1;     // ↑actif = emploi ; ↑passif = ressource
    const label = r.label || '';
    let bucket = 'autres';
    if (cat === 'wcr_asset') {
      if (/stock/i.test(label)) bucket = 'stocks';
      else if (/cr[ée]ance/i.test(label) && /client/i.test(label)) bucket = 'creances';
    } else if (/fournisseur/i.test(label)) bucket = 'fournisseurs';
    const bal = (k) => num(r.months?.[String(k + 1)]);
    for (let i = 0; i < 12; i++) {
      const prevBal = i === 0 ? openOf(r.code) : bal(i - 1);
      const impact = sign * (bal(i) - prevBal);
      out[bucket][i] += impact;
      out.total[i] += impact;
    }
  });
  return out;
};

/* Variation cumulé vs cumulé N-1 : montant absolu + taux de variation (N / N-1) − 1.
   La division garde le signe de N-1 : une charge qui recule donne un % négatif,
   comme un produit qui recule. Diviser par |N-1| inversait la lecture.

   TAUX NON SIGNIFIANT (décision 2026-08-26). Un taux de variation suppose une
   BASE POSITIVE. Sur une base négative, le rapport inverse le sens de lecture :
   une marge brute qui se dégrade de −23 à −77 s'affichait « +230 % », un EBITDA
   de −35 à −84 « +142 % » — le nombre est juste, la lecture est fausse. On
   n'affiche donc rien quand N-1 est négatif ou nul ; la variation en francs dit
   exactement ce qui s'est passé.
   Une base positive garde son taux même si N passe sous zéro (1'409 → −25 =
   −102 %) : là, le signe et l'ampleur disent bien la chute. */
const deltaOf = (a, b) => {
  if (a == null || b == null) return null;
  const abs = a - b;
  return { abs, pct: b > 0 ? (a / b - 1) * 100 : null };
};

const fmtMoney = (v, unit) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const n = Math.round(Number(v) / (unit === 'chf' ? 1 : 1000));
  return n === 0 ? '–' : n.toLocaleString('de-CH');
};
const fmtSignedMoney = (v, unit) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const n = Math.round(Number(v) / (unit === 'chf' ? 1 : 1000));
  if (n === 0) return '–';
  return (n > 0 ? '+' : '') + n.toLocaleString('de-CH');
};
// Écart d'EPT : ni CHF ni millier, un nombre de personnes à 1 décimale.
const fmtSignedFte = (v) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const n = Math.round(Number(v) * 10) / 10;
  if (n === 0) return '–';
  return (n > 0 ? '+' : '') + formatNumber(n, 1);
};
const fmtVal = (v, fmt, unit) => {
  if (v == null || !Number.isFinite(v)) return '—';
  if (fmt === 'pct') return `${formatNumber(v, 1)} %`;
  if (fmt === 'ept') return formatNumber(v, 1);   // EPT : 1 décimale, sans unité monétaire
  return fmtMoney(v, unit);
};

/* Écart vs N-1 : montant CHF (colonnes deltaAbs / deltaMAbs) ou % / points
   (deltaPct / deltaMPct). Préfixe `deltaM` = écart du mois (row.deltaM), sinon
   écart du cumulé (row.delta). Pour les lignes en % (ratios), l'écart CHF n'a
   pas de sens (→ « — ») et l'écart se lit en points.

   COULEUR (décision 2026-08-26) : un écart s'écrit en noir léger, et ne passe
   en terracotta que sur une ligne de SYNTHÈSE (sous-total, total). Sur une
   ligne de charge, un écart négatif est une baisse de coût — donc une bonne
   nouvelle, que le rouge racontait à l'envers. */
const isSummaryRow = (row) => row.kind === 'sub' || row.kind === 'total';

/* Deux absences à ne pas confondre : « — » = la DONNÉE manque, « na » = la
   mesure ne s'applique pas (un écart en francs sur une ligne de ratio, un taux
   sans base positive, un taux sur une ligne qui est déjà une variation). */
const NA = <span className="fps-cmp-na">na</span>;

function deltaText(key, row, unit) {
  const d = key.startsWith('deltaM') ? row.deltaM : row.delta;
  if (!d) return { txt: '—', neg: false };
  const red = isSummaryRow(row);
  if (key === 'deltaAbs' || key === 'deltaMAbs') {
    if (row.fmt === 'pct') return { txt: NA, neg: false };      // ratio : pas de francs
    if (d.abs == null) return { txt: '—', neg: false };
    // EPT : l'écart se compte en personnes, pas en francs (jamais divisé par mille).
    if (row.fmt === 'ept') return { txt: fmtSignedFte(d.abs), neg: red && d.abs < 0 };
    return { txt: fmtSignedMoney(d.abs, unit), neg: red && d.abs < 0 };
  }
  // deltaPct / deltaMPct
  if (row.noPct) return { txt: NA, neg: false };                // déjà une variation
  if (row.fmt === 'pct') {
    if (d.pts == null) return { txt: '—', neg: false };
    return { txt: `${d.pts > 0 ? '+' : ''}${formatNumber(d.pts, 1)} pt`, neg: red && d.pts < 0 };
  }
  if (d.pct == null) return { txt: NA, neg: false };            // base non positive
  return { txt: `${d.pct > 0 ? '+' : ''}${formatNumber(d.pct, 0)} %`, neg: red && d.pct < 0 };
}

/* Table condensée à en-tête sur deux niveaux. `groups` = [{ label, sep?, cols:[{key,
   label, highlight?}] }] ; lignes = { label, kind?, fmt?, [colKey]: value, delta }.
   Hiérarchie sub/total par la graisse ; seule la colonne `highlight` (Cumulé) est
   mise en valeur (fond phare charte). `sep` = respiration avant le groupe N-1. */
export function CompareTable({ groups, rows, unit, compact, autoWidth, fill, labelNowrap }) {
  const p = useP();
  const flatCols = groups.flatMap((grp, gi) => grp.cols.map((c, ci) => ({ ...c, sep: grp.sep && ci === 0, inSep: !!grp.sep })));
  /* Groupement standard (2026-08-17) : gouttière fixe de 16 px en tête de la
     colonne de libellés, chevron révélé au survol seulement. La gouttière ne
     s'ouvre QUE si la table porte un groupe — les autres écrans qui montent ce
     composant gardent leurs libellés là où ils sont. */
  const hasGroups = rows.some((r) => typeof r.onToggle === 'function');
  const isDeltaCol = (c) => c.key === 'deltaAbs' || c.key === 'deltaPct' || c.key === 'deltaMAbs' || c.key === 'deltaMPct';
  const cls = (c, neg, muted = false) => `fps-cmp-num${c.highlight ? ' fps-cmp-key' : ''}${c.sep ? ' fps-cmp-sep' : ''}${c.sepBefore ? ' fps-cmp-sepl' : ''}${muted ? ' fps-cmp-delta' : ''}${neg ? ' fps-cmp-neg' : ''}`;
  // Compact : colonnes resserrées pour faire tenir beaucoup de mois sur un écran ;
  // le groupe d'écart (N-1 · CHF · %) porte de plus grands nombres → plus large.
  // Colonnes d'écart (CHF / %) plus étroites : petits nombres → la colonne
  // libellé garde sa place en mode `fill` (card 950px, 8 colonnes de chiffres).
  // `fill` : la table occupe la card, la colonne libellé prend le reste — on
  // resserre donc les colonnes de chiffres pour lui laisser de quoi tenir un
  // libellé long sur une seule ligne (« Marge brute (% du CA net) »).
  const isDelta = (c) => c.key === 'deltaAbs' || c.key === 'deltaPct' || c.key === 'deltaMAbs' || c.key === 'deltaMPct';
  const wLbl = compact ? 156 : 280;
  const wCol = compact ? 54 : fill ? 84 : 96;
  const wSep = compact ? 68 : fill ? 98 : 110;
  const wDelta = compact ? 64 : fill ? 76 : 84;
  const colW = (c) => (c.sep ? wSep : isDelta(c) ? wDelta : wCol);
  // table-layout:fixed n'applique le colgroup que si la table a une largeur
  // explicite → en compact on fixe la largeur totale (sinon colonnes au contenu).
  const totalW = wLbl + flatCols.reduce((a, c) => a + colW(c), 0);
  return (
    <div className="fps-cmp-wrap">
      <table
        className={`fps-cmp${compact ? ' fps-cmp--compact' : ''}${autoWidth ? ' fps-cmp--auto' : ''}${fill ? ' fps-cmp--fill' : ''}${labelNowrap ? ' fps-cmp--lblnw' : ''}${hasGroups ? ' fps-cmp--grp' : ''}`}
        style={(compact && !autoWidth && !fill) ? { width: `${totalW}px` } : undefined}
      >
        {/* Largeurs fixes (identiques d'une table à l'autre) → les deux blocs s'alignent.
            autoWidth : colonnes calées sur le contenu. fill : table pleine largeur,
            la colonne libellé (auto) absorbe l'espace → chiffres groupés à droite. */}
        <colgroup>
          <col style={(autoWidth || fill) ? undefined : { width: `${wLbl}px` }} />
          {flatCols.map((c) => <col key={c.key} style={autoWidth ? undefined : { width: `${colW(c)}px` }} />)}
        </colgroup>
        <thead>
          <tr>
            <th className="fps-cmp-lbl" rowSpan={2}> </th>
            {groups.map((grp, gi) => (
              <th key={gi} colSpan={grp.cols.length} className={`fps-cmp-grp${grp.sep ? ' fps-cmp-sep' : ''}`}>
                {grp.label}
              </th>
            ))}
          </tr>
          <tr>
            {flatCols.map((c) => (
              <th key={c.key} className={cls(c, false)}>
                {c.label}
                {c.sub ? <span className={`fps-cmp-colsub${c.sub === 'réel' ? ' is-reel' : c.sub === 'prév.' ? ' is-prev' : ''}`}>{p(c.sub)}</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => {
            if (r.kind === 'spacer') {
              return <tr key={ri} className="fps-cmp-row--spacer"><td colSpan={flatCols.length + 1} /></tr>;
            }
            if (r.kind === 'header') {
              return (
                <tr key={ri} className="fps-cmp-row--head">
                  <td className="fps-cmp-lbl" colSpan={flatCols.length + 1}>{r.label}</td>
                </tr>
              );
            }
            const foldable = typeof r.onToggle === 'function';
            return (
            <tr key={ri}
                className={`${r.kind ? `fps-cmp-row--${r.kind}` : ''}${foldable ? ' fps-cmp-row--grp' : ''}${foldable && r.open ? ' is-open' : ''}${r.inGroup ? ' fps-cmp-row--ingrp' : ''}`}
                onClick={foldable ? r.onToggle : undefined}>
              <td className="fps-cmp-lbl">
                {hasGroups && (
                  <span className={`fps-cmp-chev${r.open ? ' is-open' : ''}`} aria-hidden="true">
                    {foldable ? '›' : ''}
                  </span>
                )}
                {r.label}
              </td>
              {flatCols.map((c) => {
                if (isDeltaCol(c)) {
                  /* Noir léger sur les lignes ENTRE les sous-totaux seulement :
                     une ligne de synthèse garde le noir plein (et le terracotta
                     si l'écart y est négatif). */
                  const { txt, neg } = deltaText(c.key, r, unit);
                  return <td key={c.key} className={cls(c, neg, !isSummaryRow(r))}>{txt}</td>;
                }
                const v = r[c.key];
                return <td key={c.key} className={cls(c, Number(v) < 0)}>{fmtVal(v, r.fmt, unit)}</td>;
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* `cutoff` — arrêté d'affichage (hub Rapports, vue Réel) : le serveur borne les
   vues N (`through`) → mois en cours, cumulés et courbes suivent l'arrêté. */
export default function FinancialsSynthesisSimple({ activeTenant, year, unit = 'kchf', onReady, framed = false, liquidityHeader = false, cutoff = null, period = null }) {
  const { t } = useTranslation();
  const p = useP();
  const MONTHS = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);
  const u = unit === 'chf' ? 'CHF' : 'kCHF';
  const tenantId = activeTenant?.id || null;
  const [pl, setPl] = useState(null);
  const [plPrev, setPlPrev] = useState(null);
  const [bs, setBs] = useState(null);
  const [bsPrev, setBsPrev] = useState(null);
  const [bsPrev2, setBsPrev2] = useState(null);
  const [indirect, setIndirect] = useState(null);
  /* Bilan PRÉVISIONNEL à plat : sa ligne de liquidités à décembre donne le
     solde projeté de fin d'exercice, que l'ex-synthèse du Prévisionnel
     annonçait en tuile. On le LIT du bilan, où le plan de liquidité se recale
     déjà : recopier le moteur du plan en ferait une seconde vérité. */
  const [bsFore, setBsFore] = useState(null);
  const [fteCur, setFteCur] = useState(null);
  const [ftePrev, setFtePrev] = useState(null);
  const [loading, setLoading] = useState(true);
  /* Le seuil minimal cible du plan de liquidité (`cash_buffer`) : le graphique de
     trésorerie le trace et permet de le régler, une seule valeur pour les deux. */
  const { a: assum, setKeyNow: setAssumNow, reload: reloadAssum } = useForecastAssumptions(tenantId, year);
  /* Décomposition du CA net : ouverte par défaut, et le choix se retient par
     client — un pli qui se rouvre à chaque retour sur l'onglet donne un
     chevron qui a l'air figé. */
  const [caOpen, setCaOpen] = usePersistentValue(`fss_ca_open_${tenantId || ''}`, true);
  /* PÉRIODE DE LECTURE (2026-09-17) : la synthèse du Prévisionnel a fusionné
     ici. Elle disait la même cascade que cette page, sur l'EXERCICE au lieu du
     réel ; deux écrans pour un compte de résultat, c'est un jour où l'on ne
     sait plus lequel fait foi. La table ne montre donc qu'un bloc de colonnes,
     et la période vient de la SECTION : la commande pilote les deux cartes,
     elle est de portée page (`PageShell views=`).
     Sans période (impression, Cockpit, rapports sur-mesure), la page garde ses
     DEUX blocs côte à côte : un PDF n'a pas de commande à cliquer. */
  const periodOn = period === 'month' || period === 'ytd' || period === 'year';

  useEffect(() => {
    if (!tenantId || !year) {
      setPl(null); setPlPrev(null); setBs(null); setBsPrev(null); setBsPrev2(null); setIndirect(null); setBsFore(null); setLoading(false);
      if (onReady) onReady();
      return;
    }
    let alive = true;
    setLoading(true);
    Promise.all([
      getPlView(tenantId, year, cutoff ? { through: cutoff } : {}),
      getPlView(tenantId, year - 1).catch(() => null),
      getBsView(tenantId, year, cutoff ? { through: cutoff } : {}).catch(() => null),
      getBsView(tenantId, year - 1).catch(() => null),
      getBsView(tenantId, year - 2).catch(() => null),
      liquidityHeader ? listIndirectCosts(tenantId, year).catch(() => null) : Promise.resolve(null),
      liquidityHeader ? getBsView(tenantId, year, { flatForecast: true }).catch(() => null) : Promise.resolve(null),
      getPlannedFte(tenantId, year).catch(() => null),
      getPlannedFte(tenantId, year - 1).catch(() => null),
    ]).then(([plR, pp, b, bp, bp2, ind, bf, fc, fp]) => {
      if (!alive) return;
      setPl(plR?.data || null);
      setPlPrev(pp?.data || null);
      setBs(b?.data || null);
      setBsPrev(bp?.data || null);
      setBsPrev2(bp2?.data || null);
      setIndirect(ind?.data || null);
      setBsFore(bf?.data || null);
      setFteCur(fc?.data || null);
      setFtePrev(fp?.data || null);
      setLoading(false);
      if (onReady) onReady();
    }).catch(() => { if (alive) { setLoading(false); if (onReady) onReady(); } });
    return () => { alive = false; };
  }, [tenantId, year, liquidityHeader, cutoff]);

  if (loading && !pl) return <p className="fps-loading">{p('Chargement de la synthèse…')}</p>;
  if (!pl) return <p className="fps-loading">{p('Aucune donnée pour cette année.')}</p>;

  const rows = pl.rows || [];
  const closed = Number.isFinite(pl.closed_count) ? pl.closed_count : 12;
  const li = closed - 1;                              // index du dernier mois clôturé
  const monthName = MONTHS[li] || '—';
  // Réel seul : on borne aux mois clôturés (au-delà = absent, pas de chiffre fabriqué).
  const clip = (arr) => (arr ? arr.map((v, i) => (i < closed ? v : null)) : null);
  /* L'EXERCICE lit les DOUZE mois de la même vue : les mois ouverts y portent
     déjà le prévisionnel (c'est la source du hub Prévisionnel, `getPlView` sans
     option). Aucun second moteur, aucune requête de plus, et l'atterrissage ne
     peut donc pas s'écarter de ce que la construction affiche. */
  const yearView = periodOn && period === 'year';
  const span = (arr) => (yearView ? arr : clip(arr));
  const plArr = (plv, code) => (plv ? span(monthsOf(plv.rows, code)) : null);

  // ── 1. Compte de résultat simplifié (flux) ───────────────────────────────
  const revenue = span(monthsOf(rows, 'sec:revenue'));
  const revenueN1 = plArr(plPrev, 'sec:revenue');
  const fluxRow = (label, arr, n1, opts = {}) => {
    const ytd = sum12(arr);
    const ytdN1 = n1 ? sum12(n1) : null;
    const month = arr ? arr[li] : null;
    const monthN1 = n1 ? n1[li] : null;
    return {
      label, ...opts,
      month, monthN1, deltaM: deltaOf(month, monthN1),
      key: ytd, ytdN1, delta: deltaOf(ytd, ytdN1),
    };
  };
  // Marge en % du CA net : ratio des cumuls (pas la moyenne des % mensuels) ; Δ en points.
  const pctRow = (label, arr, n1) => {
    const caC = sum12(revenue);
    const ytd = caC ? (sum12(arr) / caC) * 100 : null;
    let ytdN1 = null;
    if (n1 && revenueN1) { const c = sum12(revenueN1); ytdN1 = c ? (sum12(n1) / c) * 100 : null; }
    const mCa = revenue[li];
    const month = (mCa != null && arr[li] != null && Math.round(mCa) !== 0) ? (arr[li] / mCa) * 100 : null;
    const mCaN1 = revenueN1 ? revenueN1[li] : null;
    const monthN1 = (mCaN1 != null && n1 && n1[li] != null && Math.round(mCaN1) !== 0) ? (n1[li] / mCaN1) * 100 : null;
    const delta = (ytd == null || ytdN1 == null) ? null : { pts: ytd - ytdN1 };
    const deltaM = (month == null || monthN1 == null) ? null : { pts: month - monthN1 };
    return { label, fmt: 'pct', month, monthN1, deltaM, key: ytd, ytdN1, delta };
  };

  const va = span(monthsOf(rows, 'value_added'));
  const vaN1 = plArr(plPrev, 'value_added');
  const gm = span(monthsOf(rows, 'gross_margin'));
  const gmN1 = plArr(plPrev, 'gross_margin');
  const ebitda = span(monthsOf(rows, 'ebitda'));
  const ebitdaN1 = plArr(plPrev, 'ebitda');

  const netRes = span(monthsOf(rows, 'net_result'));
  const netResN1 = plArr(plPrev, 'net_result');

  /* CHARGES EN POSITIF (décision 2026-08-26). Une charge stockée en négatif
     faisait diverger les deux colonnes d'écart : l'absolu se calcule en
     DIFFÉRENCE (N − N-1), le pourcentage en RAPPORT (N ÷ N-1 − 1) ; sur une
     base négative, les deux formules donnent des signes opposés (+174 CHF et
     −75 % pour le même recul de coût). Retournées, les charges se lisent comme
     tout le reste et les deux écarts pointent dans le même sens.
     Les SOUS-TOTAUX gardent leur signe : une marge brute négative doit se lire
     négative. Le libellé porte le « − » de la cascade, pour qu'on n'additionne
     jamais une charge à un produit. */
  const flip = (arr) => (arr ? arr.map((v) => (v == null ? null : -v)) : null);
  const chargeRow = (label, code) => fluxRow(
    <>− {label}</>,
    flip(span(monthsOf(rows, code))),
    flip(plArr(plPrev, code)),
  );

  const revT = (rtype) => span(revByType(rows, rtype));
  const revTN1 = (rtype) => (plPrev ? span(revByType(plPrev.rows, rtype)) : null);
  /* Le CA net porte sa décomposition par type de revenu en GROUPE repliable, et
     le groupe s'ouvre VERS LE BAS — le geste attendu partout ailleurs.
     Ce qui manquait n'était pas le sens, c'était la FIN du bloc : « Coût des
     ventes » suivait les trois types de revenu sans que rien ne dise qu'il n'en
     était pas un. Les lignes membres portent donc un filet vertical continu,
     qui s'arrête avec elles. */
  const crRows = [
    { ...fluxRow(p('Chiffre d’affaires net'), revenue, revenueN1, { kind: 'sub' }),
      onToggle: () => setCaOpen(!caOpen), open: caOpen },
    ...(caOpen ? [
      fluxRow(p('Marchandises'), revT('marchandises'), revTN1('marchandises'), { inGroup: true }),
      fluxRow(p('Prestations'), revT('prestations'), revTN1('prestations'), { inGroup: true }),
      fluxRow(p('Autres'), revT('autres'), revTN1('autres'), { inGroup: true }),
    ] : []),
    chargeRow(p('Coût des ventes'), 'sec:cogs'),
    fluxRow(p('Valeur ajoutée'), va, vaN1, { kind: 'sub' }),
    chargeRow(p('Charges de personnel'), 'sec:personnel'),
    fluxRow(p('Marge brute'), gm, gmN1, { kind: 'sub' }),
    pctRow(p('Marge brute (% du CA net)'), gm, gmN1),
    chargeRow(p('Charges indirectes'), 'sec:other_opex'),
    fluxRow(p('EBITDA'), ebitda, ebitdaN1, { kind: 'sub' }),
    pctRow(p('EBITDA (% du CA net)'), ebitda, ebitdaN1),
    fluxRow(p('Résultat net'), netRes, netResN1, { kind: 'total' }),
  ];

  // EPT moyen + CA net par EPT moyen (sous EBITDA, précédés d'un espace).
  // 1 mois = EPT fin du dernier mois clôturé · cumulé = moyenne des mois clôturés.
  const eptArr = (fte) => (fte?.months
    ? Array.from({ length: 12 }, (_, i) => { const v = Number(fte.months[String(i + 1)]); return Number.isFinite(v) ? v : null; })
    : null);
  const eptCurM = eptArr(fteCur); const eptPrevM = eptArr(ftePrev);
  /* La moyenne d'EPT suit la PÉRIODE : douze mois sur l'exercice (le planifié
     y est déjà), les mois clos sinon. Une seule base pour les trois horizons —
     les trois tuiles « Revenu/EPT » de l'ex-synthèse du Prévisionnel en
     avaient une autre, et les deux écrans ne se recomposaient pas. */
  const avgSpan = (arr) => { if (!arr) return null; const xs = arr.slice(0, yearView ? 12 : closed).filter((v) => v != null); return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null; };
  const safeDiv = (a, b) => ((a != null && b != null && Math.abs(b) > 1e-9) ? a / b : null);
  const eptEndCur = eptCurM ? eptCurM[li] : null;
  const eptEndPrev = eptPrevM ? eptPrevM[li] : null;
  const eptAvgCur = avgSpan(eptCurM);
  const eptAvgPrev = avgSpan(eptPrevM);
  const caYtd = sum12(revenue); const caYtdN1 = revenueN1 ? sum12(revenueN1) : null;
  if (eptCurM || eptPrevM) {
    crRows.push(
      { kind: 'spacer' },
      { label: yearView ? p('EPT moyen de l’exercice') : p('EPT moyen'), fmt: 'ept',
        month: eptEndCur, monthN1: eptEndPrev, deltaM: deltaOf(eptEndCur, eptEndPrev),
        key: eptAvgCur, ytdN1: eptAvgPrev, delta: deltaOf(eptAvgCur, eptAvgPrev) },
      { label: p('CA net par EPT moyen'),
        month: safeDiv(revenue[li], eptEndCur), monthN1: safeDiv(revenueN1 ? revenueN1[li] : null, eptEndPrev),
        deltaM: deltaOf(safeDiv(revenue[li], eptEndCur), safeDiv(revenueN1 ? revenueN1[li] : null, eptEndPrev)),
        key: safeDiv(caYtd, eptAvgCur), ytdN1: safeDiv(caYtdN1, eptAvgPrev),
        delta: deltaOf(safeDiv(caYtd, eptAvgCur), safeDiv(caYtdN1, eptAvgPrev)) },
    );
  }

  // ── 2. Cash flow opérationnel — aligné EXACTEMENT sur l'onglet Flux ────────
  // Méthode indirecte : Résultat net + Amortissements (= CAF) + variation du BFR.
  // BFR sourcé de la classification Cash Flow du bilan (cf_category), comme le Flux ;
  // ouverture (mois 0) reprise de déc. N-1 : bsPrev pour N, bsPrev2 pour N-1.
  const wcrC = wcrCashImpact(bs, bsPrev);
  const wcrP = wcrCashImpact(bsPrev, bsPrev2);
  const cur = (k) => (wcrC ? clip(wcrC[k]) : null);
  const prv = (k) => (wcrP ? clip(wcrP[k]) : null);
  const addSeries = (a, b) => ((a && b) ? a.map((v, i) => (v == null || b[i] == null ? null : v + b[i])) : null);
  // Résultat net (déjà monté pour le compte de résultat) + addback des
  // amortissements (charge stockée négative → on l'ajoute).
  const netResReal = clip(monthsOf(rows, 'net_result'));
  const netResRealN1 = plPrev ? clip(monthsOf(plPrev.rows, 'net_result')) : null;
  const amort = clip(monthsOf(rows, 'sec:depreciation').map((v) => -v));
  const amortN1 = plPrev ? clip(monthsOf(plPrev.rows, 'sec:depreciation').map((v) => -v)) : null;
  // CF opérationnel = Résultat net + Amortissements + variation nette du BFR.
  const opCf = addSeries(addSeries(netResReal, amort), cur('total'));
  const opCfN1 = addSeries(addSeries(netResRealN1, amortN1), prv('total'));
  /* Les quatre lignes de BFR sont DÉJÀ des variations : un taux y serait la
     variation d'une variation, second ordre sans lecture — et ces flux oscillent
     autour de zéro par construction (une créance qui rentre passe de −87 à +42).
     Le franc EST le message : c'est l'impact sur la trésorerie. Le résultat net,
     les amortissements et le cash flow opérationnel gardent leur taux. */
  /* L'EXERCICE ne se lit pas ici (2026-09-17) : le tableau des flux se construit
     sur le bilan CONSTATÉ, et le projeter demanderait le moteur du plan de
     liquidité, qui le fait déjà mois par mois. La carte renvoie donc au plan
     plutôt que d'en recopier une seconde version. */
  const cashRows = yearView ? [
    { kind: 'header', label: p('Le plan de liquidité porte la projection, mois par mois.') },
  ] : [
    fluxRow(p('Résultat net de l’exercice'), netResReal, netResRealN1),
    fluxRow(p('Amortissements & corrections de valeur'), amort, amortN1),
    fluxRow(p('Var. créances-clients'), cur('creances'), prv('creances'), { noPct: true }),
    fluxRow(p('Var. stocks'), cur('stocks'), prv('stocks'), { noPct: true }),
    fluxRow(p('Var. fournisseurs'), cur('fournisseurs'), prv('fournisseurs'), { noPct: true }),
    fluxRow(p('Autres variations BFR'), cur('autres'), prv('autres'), { noPct: true }),
    fluxRow(p('Cash flow opérationnel'), opCf, opCfN1, { kind: 'total' }),
  ];

  // En-têtes à deux niveaux, groupés par MÉTRIQUE (pas par année) pour une lecture
  // « 1 mois → cumulé, chacun comparé à N-1 » : d'abord le mois en cours (N vs N-1
  // + écart CHF + écart %), puis un espace, puis le cumulé (N phare vs N-1 + écart
  // CHF + écart %). Chaque écart compare N à N-1 sur son propre périmètre.
  const monthBlock = { label: t('phrases:p_b1275b2599', { month: monthName }), cols: [
    { key: 'monthN1', label: `${year - 1}` },
    { key: 'month', label: `${year}`, highlight: true },
    { key: 'deltaMAbs', label: t('phrases:p_f43df0b0b1') },
    { key: 'deltaMPct', label: t('phrases:p_44487f6b50') },
  ] };
  /* Le bloc CUMULÉ porte les valeurs de la période : le cumulé des mois clos,
     ou l'exercice entier quand on le demande. Les clés ne changent donc pas,
     et `CompareTable` n'a rien à savoir de la période — seul son libellé le
     dit, et la colonne de l'exercice s'annonce « prév. ». */
  const spanBlock = (sep) => ({
    label: yearView ? `${p('Exercice')} ${year}` : t('phrases:p_cfb83eaabb', { closed }),
    sep, cols: [
      { key: 'ytdN1', label: `${year - 1}` },
      { key: 'key', label: `${year}`, highlight: true, sub: yearView ? 'réel + prév.' : undefined },
      { key: 'deltaAbs', label: t('phrases:p_f43df0b0b1') },
      { key: 'deltaPct', label: t('phrases:p_44487f6b50') },
    ],
  });
  const fluxGroups = periodOn
    ? [period === 'month' ? monthBlock : spanBlock(false)]
    : [monthBlock, spanBlock(true)];

  // Un bloc = titre + table comparée. `framed` (écran CFO) emballe chaque bloc
  // dans le gabarit Card charte (corps blanc arrondi, en-tête à filet, table
  // pleine largeur via `flush`) ; sinon bloc à plat sur le fond (print,
  // prévisionnel, rapports sur-mesure — inchangés).
  /* Son entête ne porte plus de commande : la période est de portée PAGE et
     vit dans la rangée du gabarit, au-dessus des cartes. */
  /* La PÉRIODE lue se redit en sous-titre de chaque carte : une carte se lit
     alors isolément, et une capture d'écran garde son contexte. Sans période
     (impression, Cockpit), la table porte ses deux blocs et se passe de
     sous-titre, ses entêtes de colonnes disant déjà les deux horizons. */
  const periodSub = !periodOn ? null
    : period === 'month' ? `${monthName} ${year}`
      : period === 'year' ? `${p('exercice')} ${year} · ${p('réel et prévision')}`
        : `${p('cumulé')} · ${closed} ${p('mois')}`;
  const Block = ({ title, rows: blockRows, nowrapLabels }) => {
    const head = periodSub
      ? <>{title} <span className="report-card__sub">· {periodSub}</span></>
      : title;
    /* PAS de mode `fill` (2026-09-18) : il donnait à la table 100 % de la
       carte et laissait la colonne des libellés absorber tout l'espace restant,
       si bien que « Chiffre d'affaires net » et son chiffre se retrouvaient à
       six cents pixels l'un de l'autre. Les colonnes prennent maintenant leur
       largeur déclarée (libellé 280 px, chiffres 84 à 110), la carte suit en
       `fit-content`, et le vide reste à l'extérieur. */
    const table = <CompareTable groups={fluxGroups} rows={blockRows} unit={unit} labelNowrap={nowrapLabels} />;
    if (framed) {
      return <Card className="report-card" title={head}>{table}</Card>;
    }
    return (
      <section className="fps-block">
        <div className="fps-block__head">
          <h3 className="fps-block__title">{head}</h3>
        </div>
        {table}
      </section>
    );
  };

  // ── Bandeau « Évolution de la liquidité » (option Synthèse) ───────────────
  //    Liquidité (héros gauche) et BFRE (héros droite) sur 2 lignes avec la
  //    courbe des mois réalisés en fond ; les 4 composants du BFRE en 2×2 au
  //    centre. Toutes les variations = valeur absolue depuis le 31.12.N-1 ;
  //    le % ne reste que sur la trésorerie (décision 2026-08-19). Couleur
  //    sémantique (favorable = sage ; défavorable = terracotta).
  const liqKpi = liquidityHeader ? liquiditySustainability({ bs, pl, indirect, closed }) : null;
  const nmb = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const kMoney = (v) => formatNumber(Math.round(nmb(v) / (unit === 'chf' ? 1 : 1000)), 0);


  /* CA DES DOUZE MOIS ROULANTS, finissant au mois clos : c'est la seule base
     juste pour exprimer un poste de bilan en JOURS. Le cumulé de sept mois
     donnerait 24 jours là où il y en a 16, et l'écart grandit à chaque mois
     d'écart avec l'année pleine. */
  const rev12 = (() => {
    const cur = monthsOf(rows, 'sec:revenue');
    const prv = plPrev ? monthsOf(plPrev.rows, 'sec:revenue') : null;
    if (!cur || closed < 1) return null;
    let tot = 0; let has = false;
    for (let i = closed; i < 12; i++) { const v = prv ? prv[i] : null; if (v != null) { tot += v; has = true; } }
    for (let i = 0; i < closed; i++) { const v = cur[i]; if (v != null) { tot += v; has = true; } }
    return has && Math.abs(tot) > 1e-9 ? tot : null;
  })();

  /* Solde PROJETÉ au 31.12 : lu sur la ligne standard des liquidités du bilan
     prévisionnel (jamais par son libellé, qui appartient au client). Recopier
     le moteur du plan de liquidité en ferait une seconde vérité. */
  const liqEoy = (() => {
    const row = (bsFore?.rows || []).find((r) => r.type === 'detail' && r.std_key === 'liquidity');
    const v = row?.months?.['12'];
    return Number.isFinite(Number(v)) ? Number(v) : null;
  })();

  /* Un FLUX suit la période : le mois seul quand on lit le mois, le cumul
     sinon (sept mois, ou l'exercice entier avec ses mois prévus). */
  const perVal = (arr) => (periodOn && period === 'month' ? (arr ? arr[li] : null) : sum12(arr));
  const caP = perVal(revenue); const caPN1 = perVal(revenueN1);
  const ebP = perVal(ebitda); const ebPN1 = perVal(ebitdaN1);
  const pctOf = (v, base) => ((v != null && base != null && Math.abs(base) > 1e-9) ? (v / base) * 100 : null);
  const varPct = (v, n1) => ((v != null && n1 != null && Math.abs(n1) > 1e-9) ? ((v - n1) / Math.abs(n1)) * 100 : null);
  const caVar = varPct(caP, caPN1);
  const ebMargin = pctOf(ebP, caP); const ebMarginN1 = pctOf(ebPN1, caPN1);
  const ebPts = (ebMargin != null && ebMarginN1 != null) ? ebMargin - ebMarginN1 : null;
  const signed = (v, d = 1) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(d)}`;

  /* LES DOUZE MOIS GLISSANTS d'une tuile (proto du 18.09.2026). La fenêtre
     finit au mois CLOS et remonte onze mois en arrière, à cheval sur N-1 :
     c'est la seule qui dise une tendance quel que soit le mois de l'année, là
     où l'exercice en cours n'en montre qu'un morceau en janvier. Sans bilan
     N-1, la bande se tait plutôt que de tracer une pente sur trois points. */
  const roll12 = (arrN1, arrN) => {
    if (!arrN || closed < 1) return null;
    const s24 = [...(arrN1 || Array(12).fill(null)), ...arrN];
    const last = 12 + closed - 1;
    const idxs = [];
    for (let j = last - 11; j <= last; j++) idxs.push(j);
    const serie = idxs.map((j) => (j >= 0 ? s24[j] : null));
    if (serie.filter((v) => Number.isFinite(v)).length < 2) return null;
    const lbl = (j) => {
      const m = ((j % 12) + 12) % 12;
      const yy = String(j < 12 ? year - 1 : year).slice(2);
      return `${MONTHS[m]} ${yy}`;
    };
    return { serie, labels: [lbl(idxs[0]), lbl(idxs[idxs.length - 1])] };
  };
  /* Un poste de BILAN : la valeur du mois, pas son cumul. Les postes du besoin
     en fonds de roulement se composent mois par mois, comme la tuile compose
     le sien à la date du jour. */
  const bsMonths = (view, stdKey) => {
    const l = (view?.lines || []).find((x) => x.std_key === stdKey);
    if (!l) return null;
    const r = (view?.rows || []).find((x) => x.code === l.id);
    return Array.from({ length: 12 }, (_, i) => num(r?.months?.[String(i + 1)]));
  };
  const rollCa = roll12(plPrev ? monthsOf(plPrev.rows, 'sec:revenue') : null, monthsOf(rows, 'sec:revenue'));
  const rollEb = roll12(plPrev ? monthsOf(plPrev.rows, 'ebitda') : null, monthsOf(rows, 'ebitda'));
  const rollLiq = roll12(bsMonths(bsPrev, 'liquidity'), bsMonths(bs, 'liquidity'));

  const liqCur = liqKpi?.cur?.liq;
  const atDate = `${p('au')} ${new Date(year, closed, 0).getDate()}.${String(closed).padStart(2, '0')}`;


  /* ── Trésorerie, besoin en fonds de roulement, autonomie (proto 2026-10-16) ──
     Les variations suivent la PÉRIODE lue ; le graphique de trésorerie reste sur
     douze mois glissants. Début de période : fin du mois précédent (mois) ou
     31.12 de l'exercice précédent (cumulé, exercice). Fin de période : le
     dernier mois clos, ou le 31.12 PROJETÉ pour l'exercice. */
  const per = period === 'month' ? 'month' : period === 'year' ? 'year' : 'ytd';
  const perTxt = { month: p('sur le mois'), ytd: p('depuis janvier'), year: p('sur l’exercice (projeté)') }[per];
  const startTxt = per === 'month' ? p('début du mois') : `31.12.${year - 1}`;
  const endTxt = per === 'year' ? `31.12 ${p('projeté')}` : atDate.replace(`${p('au')} `, '');
  const signedMoney = (v) => (Math.round(nmb(v) / (unit === 'chf' ? 1 : 1000)) === 0 ? '–' : `${v > 0 ? '+' : '−'}${kMoney(Math.abs(v))}`);
  const ser = liqKpi?.series || {};
  const startIdx = per === 'month' ? Math.max(0, closed - 1) : 0;
  const finite = (v) => (Number.isFinite(v) ? v : null);
  const liqStart = finite(ser.liq?.[startIdx]);
  const liqEnd = per === 'year' ? liqEoy : liqCur;
  const liqDelta = liqEnd != null && liqStart != null ? liqEnd - liqStart : null;
  /* Vue de l'EXERCICE : le graphique montre janvier à décembre, le réel puis le
     bilan prévisionnel, pour voir le solde passer sous zéro ; sans bilan
     prévisionnel, pas de courbe (jamais la fenêtre glissante à sa place). */
  const yearSerie = per === 'year' ? bsMonths(bsFore, 'liquidity') : null;
  const chartSerie = per === 'year' ? yearSerie : rollLiq?.serie;
  const liqSerie = (chartSerie || []).filter((v) => Number.isFinite(v));
  const liq12 = per !== 'year' && liqSerie.length > 1 ? liqSerie[liqSerie.length - 1] - liqSerie[0] : null;
  const liqLow = liqSerie.length ? Math.min(...liqSerie) : null;
  const rollLiqLabels = (() => {
    const last = 12 + closed - 1;
    return Array.from({ length: 12 }, (_, i) => MONTHS[(((last - 11 + i) % 12) + 12) % 12]);
  })();
  const cashBuffer = Number(assum?.cash_buffer) > 0 ? Number(assum.cash_buffer) : null;
  const saveBuffer = (v) => Promise.resolve(setAssumNow('cash_buffer', v)).then(() => reloadAssum());
  // Composantes : valeur de fin (réel ou projetée) et de début, en francs.
  const projAt = (stdKey) => { const m = bsMonths(bsFore, stdKey); return m ? Math.abs(num(m[11])) : null; };
  const compAt = (key, stdKey, idx) => {
    const v = ser[key]?.[idx];
    return Number.isFinite(v) ? Math.abs(v) : null;
  };
  const COMPS_ALL = [['rec', 'ar_clients', 'asset', p('Débiteurs')], ['inv', 'inventory', 'asset', p('Stock')],
    ['ap', 'ap_suppliers', 'debt', p('Fournisseurs')], ['adv', 'customer_advances', 'debt', p('Acomptes')]];
  const COMPS = COMPS_ALL.filter((c) => ser[c[0]]);
  const bfreRows = COMPS.map(([key, std, kind, label]) => ({
    key, label, kind,
    end: per === 'year' ? projAt(std) : compAt(key, std, closed),
    start: compAt(key, std, startIdx),
  }));
  const allKnown = bfreRows.every((r) => r.end != null && r.start != null);
  const sgnOf = (r) => (r.kind === 'debt' ? -1 : 1);
  const bfreEnd = allKnown ? bfreRows.reduce((a, r) => a + sgnOf(r) * r.end, 0) : null;
  const bfreStart = allKnown ? bfreRows.reduce((a, r) => a + sgnOf(r) * r.start, 0) : null;
  bfreRows.push({ key: 'bfre', label: p('BFRE'), kind: 'total', end: bfreEnd, start: bfreStart });
  // Autonomie : trésorerie ÷ charges fixes, et ÷ masse salariale chargée seule.
  const liqEndForAut = liqEnd;
  /* CHARGES FIXES DU MOIS, lues du compte de résultat qu'on a sous les yeux
     (2026-10-16) : la masse salariale chargée est la section du personnel, donc
     le chiffre de la ligne « Charges de personnel », au DERNIER MOIS CLOS dans
     les trois vues : c'est la charge d'aujourd'hui, la seule juste après une
     restructuration. Les charges d'exploitation, qui tombent par à-coups, se
     lissent en MOYENNE mensuelle (cumul ÷ mois clos, ou l'exercice ÷ 12). */
  const roleOfLine = {};
  (pl.lines || []).forEach((l) => { roleOfLine[l.id] = l.line_role; });
  const abs0 = (v) => Math.abs(num(v));
  const persOf = (r) => abs0(r.months?.[String(closed)]);
  const avgOf = (r) => (per === 'year' ? abs0(r.total) / 12 : (closed > 0 ? abs0(r.total_ytd) / closed : 0));
  const persRows = rows.filter((r) => r.type === 'detail' && r.section === 'personnel');
  const is13th = (r) => /13|14|treizi|quatorzi/i.test(r.label || '');
  const sumRows = (list) => list.reduce((a, r) => a + persOf(r), 0);
  const salRows = persRows.filter((r) => (roleOfLine[r.code] === 'salary' || roleOfLine[r.code] === 'bonus') && !is13th(r));
  const th13Rows = persRows.filter((r) => is13th(r));
  const socRows = persRows.filter((r) => roleOfLine[r.code] === 'social_charges');
  const othRows = persRows.filter((r) => !salRows.includes(r) && !th13Rows.includes(r) && !socRows.includes(r));
  const opexRow = rows.find((r) => r.code === 'sec:other_opex');
  /* 13ᵉ / 14ᵉ SALAIRE ET CHARGES SOCIALES CALCULÉS (2026-10-16) : la comptabilité
     peut être fausse, décalée ou provisionner autrement. On prend le TAUX
     CONSTATÉ depuis janvier et on l'applique aux salaires du DERNIER MOIS, pour
     que tout suive le salaire présenté : le 13ᵉ / 14ᵉ est un taux des salaires,
     les charges sociales un taux des salaires 13ᵉ / 14ᵉ compris. */
  const ytdOf = (list) => list.reduce((a, r) => a + abs0(r.total_ytd), 0);
  const salYtd = ytdOf(salRows);
  const th13Rate = salYtd > 0 ? ytdOf(th13Rows) / salYtd : null;
  const socRate = salYtd > 0 ? ytdOf(socRows) / (salYtd + ytdOf(th13Rows)) : null;
  const salM = sumRows(salRows); const othM = sumRows(othRows);
  /* Le client qui provisionne déjà le 13ᵉ dans ses salaires l'EXCLUT d'un geste
     (hypothèse `autonomy_exclude_13th`) : la ligne passe à zéro, sinon il serait compté deux fois. */
  const no13 = assum?.autonomy_exclude_13th === true;
  const th13M = no13 ? 0 : (th13Rate == null ? null : salM * th13Rate);
  const toggle13 = () => Promise.resolve(setAssumNow('autonomy_exclude_13th', !no13)).then(() => reloadAssum());
  const socM = socRate == null ? null : (salM + th13M) * socRate;
  const payM = socM == null ? null : salM + th13M + socM + othM;
  const opexM = opexRow ? avgOf(opexRow) : 0;
  const burnM = payM == null ? null : payM + opexM;
  const ratio = (a, b) => (a != null && b != null && b > 0 ? Math.max(0, a / b) : null);
  const autTotal = ratio(liqEndForAut, burnM);
  // « Salaires seuls » : salaires et 13ᵉ / 14ᵉ du dernier mois, sans charges sociales ni autres charges.
  const autSal = ratio(liqEndForAut, th13M == null ? null : salM + th13M);

  return (
    <div className={`fps-synth${framed ? ' fps-synth--reports' : ''}`}>
      {liqKpi && (
        <div className="fss-liq">
          <div className="fss-liq__lbl">
            <span>{p('Indicateurs')}</span>
            <small>{`${periodSub || `${p('réel')} · ${monthName} ${year}`} · ${u}`}</small>
          </div>
          <div className="fss-evo__band fss-evo__band--liq">
            <div className="fss-evo__tile fss-evo__tile--liq">
              <div className="fss-evo__lbl">{p('Trésorerie')}<span className="fss-evo__cap">{per === 'year' ? `${p('solde en fin de mois')} · ${year}` : p('solde en fin de mois, 12 mois glissants')}</span></div>
              <div className="fss-evo__val fss-evo__val--lead">{liqCur == null ? 'n/a' : kMoney(liqCur)}</div>
              {liqEoy != null && (
                <div className={`fss-evo__var${liqEoy < 0 ? ' fss-evo__var--down' : ''}`}>{p('projeté 31.12')} · {kMoney(liqEoy)}</div>
              )}
              <div className="fss-kv">
                <span>{`${p('Variation')} ${perTxt}`}</span><b className={liqDelta != null && liqDelta < 0 ? 'is-neg' : ''}>{liqDelta == null ? 'n/a' : signedMoney(liqDelta)}</b>
                {per !== 'year' && <><span>{p('Variation sur 12 mois')}</span><b className={liq12 != null && liq12 < 0 ? 'is-neg' : ''}>{liq12 == null ? 'n/a' : signedMoney(liq12)}</b></>}
                <span>{per === 'year' ? p('Point bas de l’exercice') : p('Point bas sur 12 mois')}</span><b>{liqLow == null ? 'n/a' : kMoney(liqLow)}</b>
              </div>
              <LiquidityChart serie={chartSerie} labels={per === 'year' ? MONTHS : rollLiqLabels} threshold={cashBuffer}
                              fmt={kMoney} onSave={saveBuffer} projectedFrom={per === 'year' ? closed : null} />
            </div>
            <div className="fss-evo__tile">
              <div className="fss-evo__lbl">{p('Besoin en fonds de roulement')}<span className="fss-evo__cap">{`${p('au')} ${endTxt}`}</span></div>
              <div className="fss-evo__val">{bfreEnd == null ? 'n/a' : kMoney(bfreEnd)}</div>
              <div className="fss-evo__var fss-evo__var--plain">{bfreEnd == null || !rev12 ? 'n/a' : `${formatNumber((bfreEnd / rev12) * 100, 1)} % ${p('du CAN (12 mois)')}`}</div>
              <BfreBars rows={bfreRows} fmt={kMoney} fmtSign={signedMoney} endLabel={endTxt} startLabel={startTxt} />
            </div>
            <div className="fss-evo__tile">
              <div className="fss-evo__lbl">{p('Autonomie')}<span className="fss-evo__cap">{`${p('au')} ${endTxt}`}</span></div>
              <div className="fss-aut">
                <div>
                  <div className={`fss-evo__val${autTotal != null && autTotal < 6 ? ' is-watch' : ''}`}>{autTotal == null ? 'n/a' : <>{formatNumber(autTotal, 1)} <small>{p('mois')}</small></>}</div>
                  <div className="fss-aut__t">{p('total des charges fixes')}</div>
                </div>
                <div>
                  <div className={`fss-evo__val${autSal != null && autSal < 3 ? ' is-watch' : ''}`}>{autSal == null ? 'n/a' : <>{formatNumber(autSal, 1)} <small>{p('mois')}</small></>}</div>
                  <div className="fss-aut__t">{p('salaires seuls')}</div>
                </div>
              </div>
              <div className="fss-kv fss-kv--rule">
                <span>{p('Trésorerie')}</span><b>{liqEndForAut == null ? 'n/a' : kMoney(liqEndForAut)}</b>
                <span>{p('Salaires (dernier mois)')}</span><b>{kMoney(salM)}</b>
                <span className="fss-kv__opt">
                  {p('13ᵉ / 14ᵉ salaire (calculé)')}
                  <button type="button" className="fss-kv__btn" aria-pressed={no13} onClick={toggle13}
                          title={p('À exclure si le 13ᵉ est déjà provisionné dans les salaires')}>
                    {no13 ? p('Inclure') : p('Exclure')}
                  </button>
                </span><b>{no13 ? p('exclu') : (th13M == null ? 'n/a' : kMoney(th13M))}</b>
                <span>{p('Charges sociales (calculées)')}</span><b>{socM == null ? 'n/a' : kMoney(socM)}</b>
                <span>{p('Autres charges de personnel')}</span><b>{kMoney(othM)}</b>
                <span>{p('Autres charges d’exploitation (moyenne)')}</span><b>{kMoney(opexM)}</b>
                <span className="fss-kv__tot">{p('Charges fixes / mois')}</span><b className="fss-kv__tot">{burnM == null ? 'n/a' : kMoney(burnM)}</b>
              </div>
            </div>
          </div>
        </div>
      )}

      <Block title={t('phrases:p_9d447db908')} rows={crRows} nowrapLabels />
      <Block title={t('phrases:p_513cb9f305')} rows={cashRows} />
    </div>
  );
}
